import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReplaySubject, map, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import { RingColorsService } from 'app/modules/social/services/ring-colors/ring-colors.service';
import { SnackBarComponent } from 'app/shared/snackbar/snackbar.component';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeQuestion from 'entity/BadgeQuestion';
import SimpleColorConfiguration from 'entity/SimpleColorConfiguration';
import User from 'entity/User';
import QuestionInputType from 'enums/QuestionInputType';
import { BadgeDataValue } from 'messages/websocket.messages';

export interface userInfo {
  userName?: string;
  companyName?: string;
  title?: string;
  aboutMe?: string;
  linkedIn?: string;
  department?: string;
  officeLocation?: string;
  firstName?: string;
  lastName?: string;
}

export enum StandardQuestionsTitle {
  FIRST_NAME = 'First Name',
  LAST_NAME = 'Last Name',
  EMAIL = 'Email',
  TITLE = 'Title',
  LINKEDIN = 'LinkedIn',
  COMPANY = 'Company',
  ABOUT = 'About Me',
  DEPARTMENT = 'Department',
  OFFICE = 'Office',
}

const NOTIFICATION_DURATION = 3000;

@Component({
  selector: 'app-scootaverse-badging',
  templateUrl: './scootaverse-badging.component.html',
  styleUrls: ['./scootaverse-badging.component.scss'],
  standalone: false,
})
export class ScootaverseBadgingComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  public _user = signal<User | undefined>(undefined);
  public userInfo = signal<userInfo | undefined>(undefined);
  public roomBadgeQuestions: BadgeQuestion[] = [];
  public roomVisualIndicator?: SimpleColorConfiguration;
  public loading = signal(true);
  public isOverviewing = signal(false);
  public overview: BadgeQuestion[] = [];
  protected standardQuestions: BadgeQuestion[] = [];
  protected customQuestion: BadgeQuestion[] = [];
  protected visualIndicator?: SimpleColorConfiguration;
  protected isSaving = signal(false);
  protected selectedTab = 0;

  public constructor(
    private store: Store,
    private route: ActivatedRoute,
    private badgingService: BadgingService,
    private ringColorsService: RingColorsService,
    private _snackBar: MatSnackBar,
  ) {
    super();
    this.setDisplayName('ScootaverseBadgingComponent');

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this._user.set(user);
      });
  }

  public ngOnInit(): void {
    this.store
      .select(coreSelectors.getUserAndProfileAndAccountInfo)
      .pipe(
        map(({ company, user, userProfile }) => ({
          companyName: company?.companyName,
          title: userProfile?.title,
          aboutMe: userProfile?.description,
          linkedIn: userProfile?.linkedIn,
          department: userProfile?.department,
          officeLocation: userProfile?.officeLocation,
          firstName: user.user?.firstName,
          lastName: user.user?.lastName,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe((userInfo) => {
        this.userInfo.set(userInfo);
      });

    this.getRoomBadgeQuestions()
      .catch((err) => this.info(err))
      .finally(() => this.loading.set(false));
  }

  private async getRoomBadgeQuestions() {
    const roomSetId = this.route.snapshot.paramMap.get('id');

    if (!roomSetId) {
      this.error("Error while getting room's badge questions");
      return;
    }

    const [questions, visualIndicator] = await Promise.all([
      this.badgingService.getBadgeQuestions(roomSetId),
      this.ringColorsService.getSimpleColorConfiguration(roomSetId),
    ]);

    this.roomBadgeQuestions = questions;
    this.roomVisualIndicator = visualIndicator;
  }

  protected async continue() {
    if (this.isOverviewing()) {
      await this.saveBadgeQuestions();
      return;
    }

    this.goToOverview();
  }

  private goToOverview() {
    this.overview = [...this.standardQuestions, ...this.customQuestion];
    this.isOverviewing.set(true);
  }

  private async saveBadgeQuestions() {
    try {
      this.isSaving.set(true);
      const roomSetId = this.route.snapshot.paramMap.get('id');

      if (!roomSetId) {
        this.error("Error while saving room's badge questions");
        return;
      }

      await this.badgingService.saveBadgeQuestions(roomSetId, this.overview);
      void this.saveVisualIndicators(roomSetId);
      this.openCustomSnackBar();
    } catch (err) {
      this.error('Error while saving badge questions', err);
    } finally {
      this.isSaving.set(false);
      this.isOverviewing.set(false);
    }
  }

  protected get singleSelectQuestions() {
    return this.customQuestion.filter((question) => question.inputType === QuestionInputType.SINGLE_SELECT);
  }

  protected get visualIndicatorProperty() {
    if (!this.visualIndicator) {
      return;
    }
    return this.visualIndicator.property;
  }

  protected setStandardQuestions(standardQuestions: BadgeQuestion[]) {
    this.standardQuestions = [...standardQuestions];
    this.setBadgePreview();
  }

  protected setCustomQuestion(customQuestion: BadgeQuestion[]) {
    this.customQuestion = [...customQuestion];
    this.setBadgePreview();
  }

  private saveVisualIndicators(roomSetId: string) {
    if (this.roomVisualIndicator && !this.visualIndicator) {
      return this.ringColorsService.removeSimpleColorConfiguration(roomSetId);
    }
    if (this.visualIndicator) {
      return this.ringColorsService.saveSimpleColorConfiguration(roomSetId, this.visualIndicator);
    }
  }

  private openCustomSnackBar() {
    const _data = {
      message: 'Participant questions modified correctly',
      type: 'success',
      icon: 'task_alt',
      isMatIcon: true,
    };

    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: NOTIFICATION_DURATION,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'ccustom-snackbar',
      data: _data,
    });
  }

  private setBadgePreview() {
    const badgePreview = [...this.standardQuestions, ...this.customQuestion];

    const mappedPreview: BadgeDataValue[] = badgePreview.map((question) => {
      return {
        propertyName: question.title || '',
        value: this.getBadgeAnswerFrom(question.title || ''),
        featured: !!question.featured,
      };
    });

    this.badgingService.setPreviewBadgeData(mappedPreview);
  }

  private getBadgeAnswerFrom(title: string): string {
    const titleToPropertyMap: Record<string, string | undefined> = {
      [StandardQuestionsTitle.FIRST_NAME]: this.userInfo()?.firstName,
      [StandardQuestionsTitle.LAST_NAME]: this.userInfo()?.lastName,
      [StandardQuestionsTitle.ABOUT]: this.userInfo()?.aboutMe,
      [StandardQuestionsTitle.TITLE]: this.userInfo()?.title,
      [StandardQuestionsTitle.COMPANY]: this.userInfo()?.companyName,
      [StandardQuestionsTitle.DEPARTMENT]: this.userInfo()?.department,
      [StandardQuestionsTitle.LINKEDIN]: this.userInfo()?.linkedIn,
      [StandardQuestionsTitle.OFFICE]: this.userInfo()?.officeLocation,
      [StandardQuestionsTitle.EMAIL]: 'example@email.com',
    };

    return titleToPropertyMap[title] ?? `No info`;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public formatUserName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }
}
